import { KeyboardEvent, forwardRef } from 'react';
import { Button } from '../ui/button';
import { TextArea } from '../ui/textarea';
import { Send } from 'lucide-react';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  sendDisabled?: boolean; // New prop for send-only disabling
  isSending?: boolean; // New prop to indicate sending state
  placeholder?: string;
  className?: string;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(({
  value,
  onChange,
  onSend,
  onFocus,
  disabled = false,
  sendDisabled = false,
  isSending = false,
  placeholder = 'Type a message...',
  className = '',
}, ref) => {
  // Determine if sending should be disabled
  const isSendDisabled = disabled || sendDisabled;
  
  // Determine the placeholder text
  const getPlaceholder = () => {
    if (isSending) {
      return 'Sending...';
    }
    if (isSendDisabled && !disabled) {
      return `${placeholder} (Kim is responding...)`;
    }
    return placeholder;
  };
  
  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSendDisabled && value.trim()) {
        onSend();
      }
    }
  };

  return (
    <div className={`flex space-x-2 items-end ${className}`}>
      <TextArea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={handleKeyPress}
        onFocus={onFocus}
        placeholder={getPlaceholder()}
        disabled={disabled || isSending}
        className="flex-1"
        autoResize={true}
        minRows={1}
        maxRows={6}
      />
      <Button
        onClick={onSend}
        disabled={isSendDisabled || !value.trim()}
        size="icon"
        title={
          !value.trim() 
            ? "Type a message to send" 
            : isSendDisabled 
              ? "Please wait for AI response to complete" 
              : "Send message"
        }
        className={`
          ${isSendDisabled && value.trim() 
            ? 'opacity-50 cursor-not-allowed' 
            : ''
          }
        `}
      >
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';
